/** 1.3.0 Administration → Media portal: the pieces its forms share (P1). */
import { useEffect, useState } from 'react';
import { ACCEPTANCE_MODES, type AcceptanceMode, type ReviewerDTO, type ReviewerRef } from '@prism/contracts';
import type { PortalChoicesDTO } from '../../../api/hooks/portalAdmin';

export const GIB = 1024 ** 3;

/** Bytes as GiB for a size field ("" when unset). */
export const toGib = (b: number | null | undefined): string => (b == null ? '' : String(Math.round((b / GIB) * 100) / 100));
/** A size field's GiB as bytes (null when empty). */
export const fromGib = (s: string): number | null => {
  const n = Number(s.replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(n) || n <= 0 ? null : Math.round(n * GIB);
};
export const gibLabel = (b: number | null | undefined, none = 'Portal maximum') => (b == null ? none : `${toGib(b)} GiB`);

export const ACCEPTANCE_LABEL: Record<AcceptanceMode, string> = {
  review: 'Always reviewed in Intake',
  auto_qc_pass: 'Accepted automatically when QC passes',
  auto_qc_warnings: 'Accepted automatically when QC passes, warnings allowed',
  review_on_warnings: 'Reviewed only when QC has warnings (failures go back to the supplier)',
};

/** The acceptance modes as <option>s, in the order of ACCEPTANCE_MODES (0092 added the last two). */
export function AcceptanceOptions({ reviewLabel }: { reviewLabel?: string }) {
  return <>{ACCEPTANCE_MODES.map((m) => <option key={m} value={m}>{m === 'review' && reviewLabel ? reviewLabel : ACCEPTANCE_LABEL[m]}</option>)}</>;
}

/** File extensions typed as "mxf, mov" → ['mxf', 'mov'] (lower case, no dots). */
export const parseTypes = (s: string): string[] => [...new Set(s.split(/[\s,;]+/).map((x) => x.trim().replace(/^\*?\./, '').toLowerCase()).filter(Boolean))];

/** A comma-separated list of file types, kept as typed while editing. */
export function TypesInput({ id, value, onChange, testId, placeholder = 'Any ingestible type' }: { id: string; value: string[]; onChange: (v: string[]) => void; testId?: string; placeholder?: string }) {
  const [text, setText] = useState(value.join(', '));
  useEffect(() => { if (parseTypes(text).join(',') !== value.join(',')) setText(value.join(', ')); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return <input id={id} className="input" value={text} placeholder={placeholder} data-testid={testId}
    onChange={(e) => { setText(e.target.value); onChange(parseTypes(e.target.value)); }} />;
}

export const reviewerToValue = (r: ReviewerDTO | ReviewerRef | null): string =>
  !r ? '' : r.type === 'role' ? `role:${r.key}` : `${r.type}:${r.id}`;
export const valueToReviewer = (v: string): ReviewerRef | null => {
  if (!v) return null;
  const [type, id] = [v.slice(0, v.indexOf(':')), v.slice(v.indexOf(':') + 1)];
  return type === 'role' ? { type: 'role', key: id } : { type: type as 'user' | 'group', id };
};
export const reviewerLabel = (r: ReviewerDTO) => (!r ? 'Anyone with Review intake' : r.type === 'role' ? `Role: ${r.name}` : r.type === 'group' ? `Group: ${r.name}` : r.name);

/** The reviewer: a person, an access group or a role (the task assignee model), or anyone who reviews intake. */
export function ReviewerSelect({ id, value, onChange, choices, testId }: { id: string; value: ReviewerRef | null; onChange: (v: ReviewerRef | null) => void; choices: PortalChoicesDTO | undefined; testId?: string }) {
  return (
    <select id={id} className="select" value={reviewerToValue(value)} onChange={(e) => onChange(valueToReviewer(e.target.value))} data-testid={testId}>
      <option value="">Anyone with Review intake</option>
      <optgroup label="Roles">{choices?.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.name}</option>)}</optgroup>
      <optgroup label="Access groups">{choices?.groups.map((g) => <option key={g.id} value={`group:${g.id}`}>{g.name}</option>)}</optgroup>
      <optgroup label="People">{choices?.people.map((p) => <option key={p.id} value={`user:${p.id}`}>{p.name}</option>)}</optgroup>
    </select>
  );
}

/** Fields of the metadata model, each chosen or not, and (with `required`) required or optional once chosen. */
export function FieldPicker({ choices, chosen, onChosen, required, onRequired, testId }: {
  choices: PortalChoicesDTO | undefined; chosen: string[]; onChosen: (ids: string[]) => void;
  required?: string[]; onRequired?: (ids: string[]) => void; testId?: string;
}) {
  const fields = choices?.fields ?? [];
  if (!fields.length) return <p className="muted" style={{ margin: 0 }}>The metadata model has no fields yet.</p>;
  const toggle = (list: string[], id: string, on: boolean) => (on ? [...new Set([...list, id])] : list.filter((x) => x !== id));
  return (
    <div className="portal-field-picker" data-testid={testId}>
      {fields.map((f) => {
        const on = chosen.includes(f.id);
        return (
          <div key={f.id} className="portal-field-row">
            <label className="toggle-row">
              {/* onChosen alone: the parent drops an unticked field from its required list (a second state update here
                  would be made from the same stale state and undo the first). */}
              <input type="checkbox" checked={on} onChange={(e) => onChosen(toggle(chosen, f.id, e.target.checked))} data-testid={`field-${f.id}`} />
              <span>{f.label} <span className="faint">{f.type}</span></span>
            </label>
            {required && onRequired && on && (
              <select className="select" aria-label={`${f.label}: required or optional`} value={required.includes(f.id) ? 'required' : 'optional'}
                onChange={(e) => onRequired(toggle(required, f.id, e.target.value === 'required'))} data-testid={`field-required-${f.id}`}>
                <option value="required">Required</option>
                <option value="optional">Optional</option>
              </select>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The naming rule as the API checks it: `*`, `?` and {code}, case-insensitive (references.ts namingRuleRegex). */
export function namingRuleMatches(rule: string, filename: string, code: string): boolean {
  if (!rule.trim()) return true;
  const esc = (s: string) => s.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  const body = rule.split('{code}').map((p) => esc(p).replace(/\*/g, '.*').replace(/\?/g, '.')).join(esc(code));
  try { return new RegExp(`^${body}$`, 'i').test(filename); } catch { return false; }
}
